
import React, { useState, useEffect, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { useTranslation } from 'react-i18next';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Set worker source
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PdfViewerProps {
    url: string;
}

const PdfViewer: React.FC<PdfViewerProps> = ({ url }) => {
    const { t } = useTranslation();
    const [numPages, setNumPages] = useState<number | null>(null);
    const [containerWidth, setContainerWidth] = useState<number>(800);
    const [scale, setScale] = useState<number>(1.0);

    useEffect(() => {
        const updateWidth = () => {
            const width = Math.min(window.innerWidth - 64, 850);
            setContainerWidth(width);
        };

        updateWidth();
        window.addEventListener('resize', updateWidth);
        return () => window.removeEventListener('resize', updateWidth);
    }, []);

    function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
        setNumPages(numPages);
    }

    const handleZoomIn = () => setScale(prev => Math.min(prev + 0.25, 4.0));
    const handleZoomOut = () => setScale(prev => Math.max(prev - 0.25, 0.5));
    const handleResetZoom = () => setScale(1.0);

    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0, x: 0, y: 0 });

    const onMouseDown = (e: React.MouseEvent) => {
        if (!scrollContainerRef.current) return;
        setIsDragging(true);
        setPos({
            left: scrollContainerRef.current.scrollLeft,
            top: scrollContainerRef.current.scrollTop,
            x: e.clientX,
            y: e.clientY,
        });
    };

    const onMouseMove = (e: React.MouseEvent) => {
        if (!isDragging || !scrollContainerRef.current) return;
        e.preventDefault();
        const dx = e.clientX - pos.x;
        const dy = e.clientY - pos.y;
        scrollContainerRef.current.scrollTop = pos.top - dy;
        scrollContainerRef.current.scrollLeft = pos.left - dx;
    };

    const onMouseUpOrLeave = () => {
        setIsDragging(false);
    };

    return (
        <div className="flex-1 w-full flex flex-col relative bg-gray-50 bg-brand-neutral-bg" style={{ minHeight: 0 }}>
            {/* Zoom Controls Toolbar */}
            <div className="absolute top-4 left-0 right-0 z-50 flex justify-center pointer-events-none">
                <div className="bg-white/95 backdrop-blur-md shadow-xl border border-gray-200 rounded-full flex items-center px-3 py-1.5 space-x-2 pointer-events-auto">
                    <button onClick={handleZoomOut} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-brand-purple-900 transition-colors" title={t('resources.zoom_out') as string}>
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" /></svg>
                    </button>
                    <span className="text-sm font-bold text-gray-700 w-12 text-center select-none">{Math.round(scale * 100)}%</span>
                    <button onClick={handleZoomIn} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-brand-purple-900 transition-colors" title={t('resources.zoom_in') as string}>
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                    </button>
                    <div className="w-px h-4 bg-gray-300 mx-1"></div>
                    <button onClick={handleResetZoom} className="text-[11px] font-bold text-brand-purple-600 hover:text-brand-purple-900 uppercase tracking-widest px-3 hover:bg-gray-50 rounded-full py-1.5 transition-colors" title={t('resources.reset_zoom') as string}>
                        {t('resources.reset_zoom')}
                    </button>
                </div>
            </div>

            {/* Scrollable Container */}
            <div 
                ref={scrollContainerRef}
                className={`flex-1 w-full overflow-auto text-center ${isDragging ? 'cursor-grabbing select-none' : 'cursor-grab'}`} 
                onMouseDown={onMouseDown}
                onMouseMove={onMouseMove}
                onMouseUp={onMouseUpOrLeave}
                onMouseLeave={onMouseUpOrLeave}
            >
                <div className="inline-block text-left p-4 pt-20 pb-12 align-top">
                    <div 
                        className="shadow-2xl bg-white rounded-lg overflow-hidden ring-1 ring-black/5 inline-block"
                        style={{ pointerEvents: isDragging ? 'none' : 'auto' }}
                    >
                    <Document
                        file={url}
                        onLoadSuccess={onDocumentLoadSuccess}
                        loading={
                            <div className="flex flex-col items-center justify-center p-20 space-y-4">
                                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-purple-900"></div>
                                <p className="text-gray-400 font-medium animate-pulse">{t('resources.loading_report')}</p>
                            </div>
                        }
                        error={
                            <div className="p-10 text-center text-red-500 font-bold bg-red-50 rounded-xl m-4">
                                {t('resources.pdf_load_error')}
                            </div>
                        }
                    >
                        {Array.from(new Array(numPages), (el, index) => (
                            <div
                                key={`page_${index + 1}`}
                                className="mb-6 last:mb-0 border-b border-gray-100 flex justify-center bg-gray-200"
                            >
                                <Page
                                    pageNumber={index + 1}
                                    width={containerWidth}
                                    scale={scale}
                                    renderAnnotationLayer={true}
                                    renderTextLayer={true}
                                    loading={<div className="bg-white" style={{ width: containerWidth * scale, height: (containerWidth * 1.4) * scale }} />}
                                />
                            </div>
                        ))}
                    </Document>
                </div>
            </div>
            </div>
        </div>
    );
};

export default PdfViewer;
